import React from 'react';
import { MoreVertical, Monitor, Globe, User, Package, Briefcase, FileText } from 'lucide-react';
import { AuditLogEntry, AuditEntityMapping } from '../types/audit-logs.types';

interface AuditTableProps {
  logs: AuditLogEntry[];
}

export function AuditTable({ logs }: AuditTableProps) {
  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'project': return Briefcase;
      case 'user': return User;
      case 'material': return Package;
      default: return FileText;
    }
  };

  const getActionBadge = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'update': return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'delete': return 'bg-rose-50 text-rose-600 border-rose-100';
      case 'login': return 'bg-amber-50 text-amber-600 border-amber-100';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getActionLabel = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return 'Thêm mới';
      case 'update': return 'Cập nhật';
      case 'delete': return 'Xóa';
      case 'login': return 'Đăng nhập';
      case 'logout': return 'Đăng xuất';
      default: return action;
    }
  };

  return (
    <div className="bg-white rounded-md border border-slate-100 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse sm:text-sm text-xs">
          <thead>
            <tr className="border-b border-slate-50 text-slate-400 bg-slate-50/30">
              <th className="p-3 font-normal">Thời gian</th>
              <th className="p-3 font-normal">Người dùng</th>
              <th className="p-3 font-normal">Hành động</th>
              <th className="p-3 font-normal">Đối tượng</th>
              <th className="p-3 font-normal">Mô tả</th>
              <th className="p-3 font-normal">IP & Thiết bị</th>
              <th className="p-3 font-normal">Kết quả</th>
              <th className="p-3 text-right font-normal"></th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const Icon = getEntityIcon(log.entity_type);
              const isSuccess = !log.summary?.toLowerCase().includes('lỗi') && !log.summary?.toLowerCase().includes('thất bại');
              
              return (
                <tr key={log.id} className="border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 transition-colors group">
                  <td className="p-3 whitespace-nowrap text-slate-500">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full border border-slate-200 flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                      </div>
                      {new Date(log.created_at).toLocaleString('vi-VN', { 
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit', second: '2-digit'
                      })}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-[150px]">
                      <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs overflow-hidden flex-shrink-0">
                        {log.actor?.avatar_url ? (
                          <img src={log.actor.avatar_url} className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-slate-800 font-medium truncate">{log.actor?.full_name || 'Hệ thống'}</div>
                        <div className="text-[10px] text-slate-400 truncate">{log.actor?.email || 'admin@greenspace.vn'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded border sm:text-[11px] text-[10px] ${getActionBadge(log.action_code)}`}>
                      {getActionLabel(log.action_code)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-slate-700 font-medium">{AuditEntityMapping[log.entity_type as keyof typeof AuditEntityMapping] || log.entity_type}</div>
                        <div className="text-[10px] text-slate-400">{log.project?.name || '---'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <p className="text-slate-600 line-clamp-2 max-w-[250px] leading-relaxed">
                      {log.summary}
                    </p>
                  </td>
                  <td className="p-3">
                    <div className="space-y-0.5">
                      <div className="text-slate-700 font-mono text-[11px]">{log.ip_address || '192.168.1.1'}</div>
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                        <Globe className="w-3 h-3" />
                        <span className="truncate max-w-[100px]">{log.user_agent?.split(' ')[0] || 'Browser'}</span>
                        <span className="text-slate-200">|</span>
                        <Monitor className="w-3 h-3 ml-0.5" />
                        <span>{log.device_type || 'Windows'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full sm:text-[11px] text-[10px] ${
                      isSuccess ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                    }`}>
                      {isSuccess ? 'Thành công' : 'Thất bại'}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 transition-colors">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Table Footer / Pagination Placeholder */}
      <div className="p-3 border-t border-slate-50 bg-slate-50/20 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          Hiển thị 1 - {logs.length} trong tổng số {logs.length} sự kiện
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3].map(p => (
            <button key={p} className={`w-7 h-7 rounded-md flex items-center justify-center text-xs transition-all ${
              p === 1 ? 'bg-emerald-50 text-emerald-600 font-bold border border-emerald-100' : 'text-slate-500 hover:bg-slate-100'
            }`}>
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
